import { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import ReactDOMServer from 'react-dom/server';

export const useFormatPieChartData = (options, defaultOption, value, tooltipComponent, isLowSampleSize, chartComponentRef) => {
  const [formattedChartOption, setFormattedChartOption] = useState({});
  const { centerLabel, valueRange } = options;

  useEffect(() => {
    if (options) {
      let defaultOptions = defaultOption;
      const series = [];
      series.unshift({
        type: 'pie',
        dataLabels: { enabled: false },
        enableMouseTracking: true,
        data: [
          {
            y: 1,
            name: 'hover-zone',
            color: 'transparent',
            borderWidth: 0,
            color: 'rgba(0,0,0,0)',
            opacity: 0
          }
        ],
        size: '100%',
        innerSize: '60%',
        zIndex: -1
      });
      options?.baseSegments?.forEach(e => {
        series.push({
          startAngle: e.from,
          endAngle: e.to,
          innerSize: `${e.innerSize}%`,
          size: `${e.size}%`,
          borderRadius: 20,
          data: [{ name: ' ', y: 1, color: e.color }]
        });
      });

      if (options?.chartType === 'progressGauge') {
        options?.indicatorSegments?.forEach((e, i) => {
          const { max, min } = options?.valueRange;
          const { start, end } = options?.angleRange;

          const chartValue = value;
          const progress = Math.max(0, Math.min(1, (chartValue - min) / (max - min)));
          const startAngle = start;
          const EPSILON = 0.08;
          const endAngle = start + Math.max(progress * (end - start), EPSILON);

          let indicatorColor;
          if (e?.colorRanges?.length) {
            const match = e.colorRanges.find(r => chartValue >= r.from && chartValue <= r.to);
            if (match) indicatorColor = match.color;
          }

          series.push({
            startAngle: startAngle,
            endAngle: endAngle,
            innerSize: `${e.innerSize}%`,
            size: `${e.size}%`,
            borderRadius: 20,
            data: [{ name: e?.name, y: 1, color: indicatorColor || e.color }]
          });
        });
      } else if (options?.chartType === 'performanceGauge') {
        options?.indicatorSegments?.forEach(e => {
          const { max, min } = options?.valueRange;
          const { start, end } = options?.angleRange;
          const { startAngle: topAngle = 0 } = options;

          let value = e?.value;
          if (value == 0) {
            value += 0.1;
          }
          const mid = (max + min) / 2;

          const halfRange = (end - start) / 2;
          const rangeSpan = Math.max(max - mid, mid - min);
          let deltaAngle = (Math.abs(value - mid) / rangeSpan) * halfRange;
          const MIN_GREEN_ARC = 10;

          if (deltaAngle < MIN_GREEN_ARC) {
            deltaAngle = MIN_GREEN_ARC;
          }

          const indicatorColor = e?.colorRanges?.find(r => value >= r.from && value <= r.to)?.color || e?.[0]?.color;

          let startAngle = topAngle;
          let endAngle = topAngle;
          let whiteDotStartAngle = 0;

          if (value > mid) {
            endAngle = topAngle + deltaAngle;
            whiteDotStartAngle = endAngle - 9;
            startAngle -= 3.5;
          } else if (value < mid) {
            endAngle = topAngle - deltaAngle;
            const temp = startAngle;
            startAngle = endAngle;
            endAngle = temp;
            whiteDotStartAngle = startAngle + 3;
            endAngle += 3.5;
          }
          series.push({
            startAngle,
            endAngle,
            innerSize: `${options?.indicatorSegments?.[0]?.innerSize}%`,
            size: `${e.size}%`,
            borderRadius: 20,
            data: [{ name: e?.name, y: 1, color: indicatorColor }]
          });
          series.push({
            startAngle: whiteDotStartAngle,
            endAngle: whiteDotStartAngle + 6,
            size: '100%',
            innerSize: '90%',
            borderRadius: 100,
            data: [{ name: 'Segment 4', y: 1, color: 'white' }]
          });
        });
      }

      if (tooltipComponent) {
        defaultOptions = Highcharts.merge(defaultOptions, {
          tooltip: {
            useHTML: true,
            hideDelay: 0,
            animation: false,
            fadeOut: 0,
            shared: true,
            backgroundColor: 'transparent',
            shadow: false,
            padding: 0,
            formatter: function () {
              return ReactDOMServer.renderToString(tooltipComponent({ value: this.point?.y }));
            }
          }
        });
      }
      defaultOptions = Highcharts.merge(defaultOptions, {
        chart: {
          ...defaultOptions.chart,
          margin: [5, 0, 40, 0],
          height: 250,
          events: {
            load() {
              drawCustomLabels(this, options, isLowSampleSize);
              // updateCenterLabel(this);
            },
            redraw() {
              drawCustomLabels(this, options, isLowSampleSize);
              // updateCenterLabel(this);
            }
          }
        },
        // annotations: dummyAnnotations,
        series
      });
      setFormattedChartOption(defaultOptions);
    }
  }, [options]);
  return { formattedChartOption };
};

function updateCenterLabel(chart) {
  const centerX = chart.plotLeft + chart.plotWidth / 2;
  const centerY = chart.plotTop + chart.plotHeight / 2;

  chart.annotations.forEach(annotation => {
    const updatedLabels = annotation.options.labels.map(label => {
      const yOffset = label.offsetY || 0;
      const xOffset = label.offsetX || 0;
      return {
        ...label,
        point: {
          x: centerX + xOffset,
          y: centerY + yOffset,
          xAxis: null,
          yAxis: null
        }
      };
    });
    annotation.update({ labels: updatedLabels });
  });
}
const drawCustomLabels = (chart, options, isLowSampleSize) => {
  const { centerLabel, valueRange } = options;
  if (!centerLabel) return;

  const centerX = chart.plotWidth / 2 + chart.plotLeft;
  const centerY = chart.plotHeight / 2 + chart.plotTop;
  const radius = Math.min(chart.plotWidth, chart.plotHeight) / 2;
  const offsetBottom = -20;
  const offsetX = radius * 0.4;
  const gaugeBottomY = centerY + radius + offsetBottom;

  chart.customCenterLabel?.destroy();
  chart.customSubLabel?.destroy();
  chart.bottomLabel1?.destroy();
  chart.bottomLabel2?.destroy();

  chart.customCenterLabel = chart.renderer
    .text(Number(centerLabel?.label || 0).toFixed(2) ?? '', centerX, centerY + 10)
    .attr({ align: 'center' })
    .css({ color: '#000', fontSize: '30px', fontWeight: 'bold' })
    .add();
  if (isLowSampleSize) {


    chart.customCenterLabel = chart.renderer
      .html(
        `<div
  style="
    position: relative;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1.5px solid #D32F2F;
    overflow: hidden;
    flex-shrink: 0;
  "
>
  <div
    style="
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50%;
      background-color: #D32F2F;
    "
  ></div>
</div>
`,
        centerX - 60,
        centerY + 8
      )
      .add();
  }
  chart.customSubLabel = chart.renderer
    .text(centerLabel.subLabel ?? '', centerX, gaugeBottomY - 10)
    .attr({ align: 'center' })
    .css({ color: '#666', fontSize: '14px' })
    .add();

  chart.bottomLabel1 = chart.renderer
    .text(valueRange?.min ?? '', centerX - offsetX, gaugeBottomY + 40)
    .attr({ align: 'center' })
    .css({ color: '#333', fontSize: '14px', fontWeight: '500' })
    .add();

  chart.bottomLabel2 = chart.renderer
    .text(valueRange?.max ?? '', centerX + offsetX, gaugeBottomY + 40)
    .attr({ align: 'center' })
    .css({ color: '#333', fontSize: '14px', fontWeight: '500' })
    .add();
};
